import { PageHeader } from "@/components/PageHeader";
import { InlineError } from "@/components/QueryState";
import { AskAdminsDialog } from "@/components/grants/AskAdminsDialog";
import { ConnectionSheet } from "@/components/grants/ConnectionSheet";
import { GrantList } from "@/components/grants/GrantList";
import { ManageGrantSheet } from "@/components/grants/ManageGrantSheet";
import { RequestAccessSheet } from "@/components/grants/RequestAccessSheet";
import { ReviewRequestSheet } from "@/components/grants/ReviewRequestSheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, api, type BooLinkRow } from "@/lib/api";
import { needsApproval } from "@/lib/grant-scopes";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowUpRight, Inbox, Plus } from "lucide-react";
import { useState } from "react";

// Copy vocabulary: a Boo "learns from" other Boos and "shares with" them. A
// Boo's admins ask for access by depth; the other Boo's admins approve it item
// by item; what was granted can be narrowed by either side at any time.
//
// Deep links (from chat and emails), all on this tab:
//   ?request=<sourceId>     open "Learn from another Boo" with that Boo picked
//   ?request=1&find=<name>  the same, with the picker filtered by name
//   ?source=<sourceId>      open this Boo's connection to that source
//   ?review=<linkId>        open a waiting request for this Boo's admins

export type SharingSearch = { request?: string; find?: string; source?: string; review?: string };

const connectionsKey = (containerId: string) => ["connections", containerId] as const;

type Open =
  | { kind: "request"; sourceId?: string | null; find?: string | null }
  | { kind: "connection"; linkId: string }
  | { kind: "review"; linkId: string }
  | { kind: "grant"; linkId: string }
  | { kind: "ask"; sourceId?: string | null }
  | null;

/**
 * "Sharing" tab: the Boos this one learns from, and the Boos it shares with.
 * The same list and sheets as the org's access page, with only this Boo's grants.
 */
export function SharingTab() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId!;
  const orgId = params.orgId!;
  const search = useSearch({ strict: false }) as SharingSearch;
  const navigate = useNavigate();

  const boosQuery = useQuery({ queryKey: ["containers"], queryFn: () => api.containers.list() });
  const boos = boosQuery.data?.data ?? [];
  const thisBoo = boos.find((b) => b.id === containerId);
  const thisRunning = thisBoo?.deploymentStatus === "running";
  const orgPerms = useQuery({ queryKey: ["org-permissions", orgId], queryFn: () => api.org.permissions() });
  const query = useQuery({
    queryKey: connectionsKey(containerId),
    queryFn: () => api.containers.connections.list(containerId),
    retry: (failureCount, error) => !(error instanceof ApiError && [403, 404].includes(error.status)) && failureCount < 2,
    // Approved links set themselves up in the background, and removals land
    // when the other Boo next syncs; watch until they do.
    refetchInterval: (q) =>
      thisRunning &&
      [...(q.state.data?.using ?? []), ...(q.state.data?.sharing ?? [])].some(
        (l) => (l.status === "approved" && !l.setUp && !l.setUpFailed && l.source?.running !== false) || !!l.removalPendingSince,
      )
        ? 4000
        : false,
  });
  const thisName = thisBoo?.name ?? "This Boo";
  const canManage = query.data?._permissions?.canManage === true;
  const using = query.data?.using ?? [];
  const sharing = query.data?.sharing ?? [];
  const waiting = sharing.filter(needsApproval);
  const shared = sharing.filter((l) => l.status === "approved" || l.removalPendingSince);

  const [picked, setPicked] = useState<Open>(null);
  const [linkDismissed, setLinkDismissed] = useState(false);

  // A deep link opens its sheet once the data is in; the URL keeps it until
  // the sheet is closed.
  const fromUrl = ((): Open => {
    if (!query.data || !boosQuery.data || linkDismissed) return null;
    let next: Open = null;
    if (search.review) {
      const link = sharing.find((l) => l.id === search.review);
      if (link) next = needsApproval(link) ? { kind: "review", linkId: link.id } : link.status === "approved" ? { kind: "grant", linkId: link.id } : null;
    } else if (search.source) {
      const link = using.find((l) => l.source?.id === search.source && l.status !== "revoked");
      next = link ? { kind: "connection", linkId: link.id } : { kind: "request", sourceId: search.source };
    } else if (search.request) {
      const sourceId = boos.some((b) => b.id === search.request) ? search.request : null;
      const existing = sourceId ? using.find((l) => l.source?.id === sourceId && l.status !== "revoked") : undefined;
      // Already asked or connected: show where it stands instead of a second ask.
      next = existing && existing.status !== "declined" ? { kind: "connection", linkId: existing.id } : { kind: "request", sourceId, find: search.find ?? null };
    }
    // Someone who can't ask (a member, or arriving from chat) asks the admins instead.
    if (next && !canManage && next.kind === "request") return { kind: "ask", sourceId: next.sourceId };
    // Reviewing and changing a grant are for those who can manage, as in the lists below.
    return next && !canManage && (next.kind === "review" || next.kind === "grant") ? null : next;
  })();

  const open = picked ?? fromUrl;
  const setOpen = (next: Open) => {
    setPicked(next);
    if (!next && (search.review || search.source || search.request)) {
      setLinkDismissed(true);
      void navigate({ to: ".", search: {}, replace: true } as never);
    }
  };

  const learnsFrom = using.filter((l) => l.status !== "revoked");
  // A grant this Boo gives: its admins review or change it; everyone else sees where it stands.
  const openShared = (l: BooLinkRow) =>
    setOpen(
      canManage && needsApproval(l)
        ? { kind: "review", linkId: l.id }
        : canManage && l.status === "approved"
          ? { kind: "grant", linkId: l.id }
          : { kind: "connection", linkId: l.id },
    );
  const linkById = (id: string) => [...using, ...sharing].find((l) => l.id === id);
  const openLink = open && "linkId" in open ? linkById(open.linkId) : undefined;
  const actor = { kind: "boo" as const, containerId };

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeader title="Sharing" />
        {orgPerms.data?._permissions?.canViewAccess && (
          <Link to="/orgs/$orgId/access" params={{ orgId }} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            All sharing in Boo Org
            <ArrowUpRight className="size-3.5" />
          </Link>
        )}
      </div>
      {query.isLoading ? (
        <SharingSkeleton />
      ) : query.isError ? (
        <div className="flex flex-col items-start gap-2">
          <InlineError error={query.error} />
          <Button variant="outline" size="sm" disabled={query.isFetching} onClick={() => query.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          {waiting.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Inbox className="size-4 text-muted-foreground" aria-hidden />
                Needs your approval
                <span className="rounded-full bg-foreground px-1.5 text-xs font-medium text-background tabular-nums">{waiting.length}</span>
              </h2>
              <GrantList
                links={waiting}
                onOpen={openShared}
                empty={null}
                side="source"
                action={
                  canManage
                    ? (l) => (
                        <Button size="sm" onClick={() => setOpen({ kind: "review", linkId: l.id })}>
                          Review
                        </Button>
                      )
                    : undefined
                }
              />
            </section>
          )}

          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">Learns from</h2>
              {canManage ? (
                <Button size="sm" onClick={() => setOpen({ kind: "request" })}>
                  <Plus />
                  Learn from another Boo
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setOpen({ kind: "ask" })}>
                  Ask an admin
                </Button>
              )}
            </div>
            <GrantList links={learnsFrom} onOpen={(l) => setOpen({ kind: "connection", linkId: l.id })} empty={`${thisName} doesn't learn from any other Boo yet.`} />
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-base font-semibold">Shares with</h2>
            <GrantList
              links={shared}
              onOpen={openShared}
              side="source"
              empty={
                sharing.length === 0
                  ? `No other Boo learns from ${thisName} yet. Another Boo's admin can ask from its Sharing tab.`
                  : `No other Boo learns from ${thisName} right now.`
              }
            />
          </section>
        </>
      )}

      {open?.kind === "request" && (
        <RequestAccessSheet
          consumer={{ id: containerId, name: thisName }}
          candidates={boos}
          initialSourceId={open.sourceId}
          find={open.find}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === "connection" && openLink && (
        <ConnectionSheet
          key={openLink.id}
          containerId={openLink.consumer?.id ?? containerId}
          link={openLink}
          // From the sharing side it only shows where things stand.
          canManage={canManage && openLink.consumer?.id === containerId}
          note={canManage ? undefined : "Only this Boo's owners and admins can change this."}
          onAskMore={() => setOpen({ kind: "request", sourceId: openLink.source?.id })}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === "ask" && (
        <AskAdminsDialog boo={{ id: containerId, name: thisName }} candidates={boos} initialSourceId={open.sourceId} onClose={() => setOpen(null)} />
      )}
      {open?.kind === "review" && openLink && <ReviewRequestSheet key={openLink.id} actor={actor} link={openLink} onClose={() => setOpen(null)} />}
      {open?.kind === "grant" && openLink && <ManageGrantSheet key={openLink.id} actor={actor} link={openLink} onClose={() => setOpen(null)} />}
    </div>
  );
}

function SharingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-14 w-full rounded-lg" />
      <Skeleton className="h-14 w-full rounded-lg" />
    </div>
  );
}
